// src/components/Mining/Mining.js

import React, { useState, useEffect, useContext, useCallback } from 'react';
import { MiningContext } from '../../context/MiningContext';
import { StakingContext } from '../../context/StakingContext';
import { WalletContext } from '../../context/WalletContext';
import {
  Typography,
  Button,
  CircularProgress,
  TextField,
  Box,
} from '@mui/material';
import { toast } from 'react-toastify';
import xLogo from '../../assets/xlogo.svg';
import { ethers } from 'ethers';

// Utility formatting functions
const formatNumber = (num) => {
  const number = parseFloat(num);
  if (number >= 1e12) return (number / 1e12).toFixed(1) + 'T';
  if (number >= 1e9) return (number / 1e9).toFixed(1) + 'B';
  if (number >= 1e6) return (number / 1e6).toFixed(1) + 'M';
  if (number >= 1e3) return (number / 1e3).toFixed(1) + 'K';
  return number.toFixed(1);
};

const formatEth = (num) => {
  const number = parseFloat(num);
  if (isNaN(number)) return '0.00';
  return number.toFixed(2);
};

const formatTimeShort = (seconds) => {
  if (seconds <= 0) return '0:00:00:00';
  const d = Math.floor(seconds / (24 * 3600));
  const h = Math.floor((seconds % (24 * 3600)) / 3600);
  const m = Math.floor((seconds % 3600) / 60);
  const s = Math.floor(seconds % 60);
  return `${d}:${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
};

const glitchSplit = (text) => {
  return text.split('').map((char, i) => {
    if (char === ' ') return <span key={`space-${i}`}>&nbsp;</span>;
    return (
      <span key={i} className="char soft-glitch" data-char={char}>
        {char}
      </span>
    );
  });
};

// -- Share modals etc. omitted for brevity (they remain the same)...

const ShareOnXModal = React.memo(({ onClose, minerNumber, timesMined, miningRewards }) => {
  const tweetText = `Miner #${minerNumber}\nTimes Mined: ${timesMined}\n$CASH earned: ${miningRewards} \nJoin The Revolution Of Currency\n@_CASHisDEAD_`;
  const shareUrl = `https://twitter.com/intent/tweet?text=${encodeURIComponent(tweetText)}`;

  const modalRef = React.useRef();

  const handleClickOutside = useCallback(
    (e) => {
      if (modalRef.current && !modalRef.current.contains(e.target)) {
        onClose();
      }
    },
    [modalRef, onClose]
  );

  useEffect(() => {
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [handleClickOutside]);

  const handleClose = (e) => {
    e.stopPropagation();
    onClose();
  };

  return (
    <div className="modal-overlay">
      <div className="modal-content" ref={modalRef}>
        <button className="modal-close-button" onClick={handleClose} aria-label="Close Modal">
          &times;
        </button>
        <div className="share-on-x-prompt">
          <img src={xLogo} alt="Share on X" className="share-logo" />
          <span className="share-message">Share your mining achievement!</span>
          <div style={{ display: 'flex', gap: '10px', justifyContent: 'center', marginTop: '20px' }}>
            <a href={shareUrl} target="_blank" rel="noopener noreferrer" className="share-button">
              <Button variant="contained" color="primary">Share</Button>
            </a>
            <a
              href="https://twitter.com/_CASHisDEAD_"
              target="_blank"
              rel="noopener noreferrer"
              className="follow-button"
            >
              <Button variant="outlined" color="secondary">Follow</Button>
            </a>
          </div>
        </div>
      </div>
    </div>
  );
});

const ShareMinerNumberModal = React.memo(({ onClose, minerNumber }) => {
  const shareText = encodeURIComponent(
    `I'm Miner #${minerNumber} 💸\n\n@_CASHisDEAD_`
  );
  const shareUrl = `https://twitter.com/intent/tweet?text=${shareText}`;

  const handleOverlayClick = (e) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  return (
    <div className="modal-overlay" onClick={handleOverlayClick}>
      <div className="modal-content">
        <button className="close-button" onClick={onClose}>X</button>
        <Typography variant="h6">Share your Miner status!</Typography>
        <Button
          variant="contained"
          color="primary"
          href={shareUrl}
          target="_blank"
          rel="noopener noreferrer"
          startIcon={<img src={xLogo} alt="Share on X" className="share-logo" />}
        >
          Share on X
        </Button>
      </div>
    </div>
  );
});

const StakingConfirmationModal = React.memo(({ onConfirm, onCancel, rewardAmount, approving }) => {
  // ... same content as you had
  // omitted for brevity
});

const Mining = () => {
  const {
    miningEventStatus,
    claimMiningReward,
    contribute,
    fetchStakingPositions,
    fetchUserMiningData,
    userContributions,
    userReward,
    hasClaimedReward,
    userBoost,
    stakingContractRef,
    tokenContractRef,
    showStakingModal,
    setShowStakingModal,
    totalMiners
  } = useContext(MiningContext);

  const { stakeTokens } = useContext(StakingContext);
  const { provider, account } = useContext(WalletContext);

  const [loading, setLoading] = useState(false);
  const [approving, setApproving] = useState(false);
  const [contributionAmount, setContributionAmount] = useState('');
  const [showShareModal, setShowShareModal] = useState(false);
  const [showMinerShareModal, setShowMinerShareModal] = useState(false);
  const [timeLeft, setTimeLeft] = useState(0);

  // Timer for event countdown
  useEffect(() => {
    if (miningEventStatus?.isActive) {
      const timer = setInterval(() => {
        if (!miningEventStatus.isActive || miningEventStatus.endTime <= 0) {
          setTimeLeft(0);
        } else {
          const now = Math.floor(Date.now() / 1000);
          setTimeLeft(Math.max(miningEventStatus.endTime - now, 0));
        }
      }, 1000);
      return () => clearInterval(timer);
    } else if (miningEventStatus?.finalized) {
      setTimeLeft(0);
    }
  }, [miningEventStatus]);

  const isEventLive = miningEventStatus?.isActive && timeLeft > 0;
  const isEventFinalized = miningEventStatus?.finalized && !miningEventStatus?.isActive;

  const handlePercentageClick = useCallback(
    async (percentage) => {
      try {
        const balance = await provider.getBalance(account);
        const bal = parseFloat(ethers.utils.formatEther(balance));
        const calc = (bal * percentage).toFixed(2);
        setContributionAmount(calc);
      } catch (error) {
        const errMsg = (error.message || '').toLowerCase();
        const isCommonError = 
          errMsg.includes('rate limit') ||
          errMsg.includes('bad result') ||
          errMsg.includes('blockchain height');
        
        if (!isCommonError) {
          console.error('Error getting balance:', error);
          toast.error('Failed to get balance');
        }
      }
    },
    [account, provider]
  );

  const handleContribute = useCallback(async () => {
    if (!account || !provider) {
      toast.error('Please connect your wallet');
      return;
    }
    if (!contributionAmount || parseFloat(contributionAmount) <= 0) {
      toast.error('Invalid contribution amount.');
      return;
    }
    try {
      setLoading(true);
      const balance = await provider.getBalance(account);
      const formattedBalance = parseFloat(ethers.utils.formatEther(balance));
      const amountInWei = ethers.utils.parseEther(contributionAmount.toString());

      if (formattedBalance < parseFloat(contributionAmount)) {
        toast.error('Insufficient ETH balance for contribution.');
        return;
      }
      if (!stakingContractRef?.current) {
        toast.error('Staking contract not loaded.');
        return;
      }

      await contribute(amountInWei);
      await fetchUserMiningData();
      setContributionAmount('');
      setShowMinerShareModal(true);
    } catch (error) {
      if (
        !error.message.includes('User rejected') &&
        !error.message.includes('user rejected') &&
        !error.message.includes('user denied')
      ) {
        console.error('Error contributing:', error);
        toast.error(error.message || 'Failed to contribute');
      }
    } finally {
      setLoading(false);
    }
  }, [account, provider, contributionAmount, stakingContractRef, contribute, fetchUserMiningData]);

  const handleClaimMiningReward = useCallback(async () => {
    if (!account || !provider) {
      toast.error('Please connect your wallet');
      return;
    }
    if (loading) {
      toast.warn('Another transaction is in progress. Please wait.');
      return;
    }
    try {
      setLoading(true);
      const success = await claimMiningReward();
      if (success) {
        setShowShareModal(true);
      }
    } catch (error) {
      if (
        !error.message.includes('User rejected') &&
        !error.message.includes('user rejected') &&
        !error.message.includes('user denied')
      ) {
        console.error('Error claiming reward:', error);
        toast.error(error.message || 'Failed to claim reward');
      }
    } finally {
      setLoading(false);
    }

    // Optional check for staking cutoff
    const stakingContract = stakingContractRef.current;
    if (stakingContract) {
      try {
        const startTime = await stakingContract.stakingContractStartTime();
        const currentTime = Math.floor(Date.now() / 1000);
        const stakeCutoff = 3 * 24 * 60 * 60;
        const deadline = startTime.toNumber() + stakeCutoff;

        if (currentTime <= deadline) {
          setShowStakingModal(true);
        }
      } catch (error) {
        console.error('Error checking staking period:', error);
      }
    }
  }, [account, provider, loading, claimMiningReward, stakingContractRef, setShowStakingModal]);

  const handleStakeReward = useCallback(async () => {
    try {
      setLoading(true);
      await stakeTokens(userReward, 0);
      setShowStakingModal(false);
    } catch (error) {
      if (
        !error.message.includes('User rejected') &&
        !error.message.includes('user rejected') &&
        !error.message.includes('user denied')
      ) {
        console.error('Error staking reward:', error);
        toast.error(error.message || 'Failed to stake reward');
      }
    } finally {
      setLoading(false);
    }
  }, [stakeTokens, userReward, setShowStakingModal]);

  const handleStakingCancel = useCallback(() => {
    setShowStakingModal(false);
  }, [setShowStakingModal]);

  const handleStake = useCallback(async () => {
    if (!userReward || parseFloat(userReward) <= 0) {
      toast.warn('No rewards to stake.');
      return;
    }
    try {
      const parsedRewardBN = ethers.BigNumber.from(ethers.utils.parseEther(userReward));
      const oneTokenBN = ethers.utils.parseUnits('1', 18);

      if (parsedRewardBN.lte(oneTokenBN)) {
        toast.error('Cannot stake entire reward. Keep at least 1 $CASH for fees.');
        return;
      }
      const finalStakeBN = parsedRewardBN.sub(oneTokenBN);
      const finalStakeStr = ethers.utils.formatUnits(finalStakeBN, 18);

      const balance = await provider.getBalance(account);
      const parsedCashBalance = parseFloat(ethers.utils.formatEther(balance));
      if (parseFloat(finalStakeStr) > parsedCashBalance) {
        toast.error('Insufficient $CASH balance to stake that amount.');
        return;
      }

      setApproving(true);
      await handleStakeReward();
      await fetchStakingPositions();
    } catch (error) {
      console.error('Staking: Error during staking:', error);
      if (
        !error.message.includes('User rejected') &&
        !error.message.includes('user rejected') &&
        !error.message.includes('user denied')
      ) {
        toast.error('Failed to stake rewards. Please try again.');
      }
    } finally {
      setApproving(false);
    }
  }, [userReward, provider, account, handleStakeReward, fetchStakingPositions]);

  const handleApprovalAndStake = useCallback(async () => {
    if (!account || !stakingContractRef?.current || !tokenContractRef?.current || !provider) {
      console.error('Required contracts or account not initialized');
      return;
    }
    try {
      const signer = provider.getSigner();
      const stakingContract = stakingContractRef.current;
      const tokenContract = tokenContractRef.current;

      const allowance = await tokenContract.allowance(account, stakingContract.address);
      const requiredAllowance = ethers.utils.parseEther('1000000000');

      if (allowance.lt(requiredAllowance)) {
        setApproving(true);
        const tx = await tokenContract
          .connect(signer)
          .approve(stakingContract.address, requiredAllowance, { gasLimit: 100000 });
        await tx.wait();
      }

      await handleStake();
    } catch (error) {
      console.error('Error during approval process:', error);
      toast.error('Failed to approve tokens');
    } finally {
      setApproving(false);
    }
  }, [account, stakingContractRef, tokenContractRef, provider, handleStake]);

  /**
   * Renders main content depending on event status
   */
  const renderContent = useCallback(() => {
    if (miningEventStatus?.finalized) {
      return (
        <div className="finalized-event-section">
          <Typography variant="h5" gutterBottom>
            🎉 Mining Event Completed
          </Typography>
          <Typography variant="body1" gutterBottom>
            Your Miner Number:{' '}
            <span className="countdown-number">#{userContributions.minerNumber}</span>{' '}
            of{' '}
            <span className="countdown-number">{totalMiners}</span>{' '}
            Total Miners
          </Typography>

          {/* Place top-100 & last-hour as subtle italic lines under "Total Contributions" */}
          <Typography variant="body1" gutterBottom>
            Total Contributions:{' '}
            <span className="countdown-number">
              {userContributions.contributionCount}
            </span>{' '}
            times
            {(userContributions.minerNumber > 0 && userContributions.minerNumber <= 100) && (
              <Typography
                variant="caption"
                sx={{
                  display: 'block',
                  fontStyle: 'italic',
                  color: '#ffcc00',
                  mt: 0.5,
                }}
              >
                *You were among the first 100 miners (+10% bonus)*
              </Typography>
            )}
            {userContributions.contributedLastHour && (
              <Typography
                variant="caption"
                sx={{
                  display: 'block',
                  fontStyle: 'italic',
                  color: '#ffcc00',
                  mt: 0.5,
                }}
              >
                *You contributed in the last hour (+10% bonus)*
              </Typography>
            )}
          </Typography>

          <Typography variant="body1" gutterBottom>
            Amount Contributed:{' '}
            <span className="countdown-number">
              {formatEth(userContributions.contributionAmount)}
            </span>{' '}
            ETH
          </Typography>

          {/* Claim reward button, etc. */}
          {userReward && parseFloat(userReward) > 0 && !hasClaimedReward && (
            <Button
              variant="contained"
              color="primary"
              onClick={handleClaimMiningReward}
              disabled={loading}
              className="claim-button"
              fullWidth
            >
              {loading ? <CircularProgress size={24} /> : 'Claim Reward'}
            </Button>
          )}

          {hasClaimedReward && (
            <Typography variant="body1" gutterBottom style={{ color: '#4CAF50', marginTop: '10px' }}>
              ✅ Reward Successfully Claimed!
            </Typography>
          )}

          {/* Share on X */}
          <div className="follow-us-section" style={{ marginTop: '20px' }}>
            <Button
              variant="contained"
              color="primary"
              onClick={() => setShowShareModal(true)}
              className="share-button"
              startIcon={<img src={xLogo} alt="Share on X" className="share-logo" />}
              fullWidth
            >
              Share on X
            </Button>
          </div>
        </div>
      );
    }

    if (!miningEventStatus?.isActive) {
      // No active event
      return (
        <div className="no-active-event">
          <Typography variant="h5" gutterBottom>
            🌟 Mining Event
          </Typography>
          <Typography variant="body1" gutterBottom>
            No active mining event at the moment.
          </Typography>
          <Typography variant="body2" gutterBottom>
            Stay tuned...
          </Typography>
        </div>
      );
    }

    // If event is active
    return (
      <div className="active-event">
        <Typography variant="h5" gutterBottom>
          ⛏️ Active Mining Event
        </Typography>
        <Typography variant="body1" gutterBottom>
          My Contributions: {userContributions.contributionCount} times
        </Typography>
        <Typography variant="body1" gutterBottom>
          Amount Contributed:{' '}
          {formatEth(userContributions.contributionAmount || '0')} ETH
        </Typography>

        {/* Subtle, italic lines under 'Total Contributions' */}
        <Typography variant="body1" gutterBottom>
          Total Contributions: {userContributions.contributionCount}
          {(userContributions.minerNumber > 0 && userContributions.minerNumber <= 100) && (
            <Typography
              variant="caption"
              sx={{
                display: 'block',
                fontStyle: 'italic',
                color: '#ffcc00',
                mt: 0.5,
              }}
            >
              *You are among the first 100 miners (+10% bonus)*
            </Typography>
          )}
          {userContributions.contributedLastHour && (
            <Typography
              variant="caption"
              sx={{
                display: 'block',
                fontStyle: 'italic',
                color: '#ffcc00',
                mt: 0.5,
              }}
            >
              *You contributed in the last hour (+10% bonus)*
            </Typography>
          )}
        </Typography>

        <div className="contribution-controls" style={{ marginTop: '10px' }}>
          <Box
            className="contribution-section"
            sx={{
              maxWidth: '300px',
              margin: '0 auto',
            }}
          >
            <TextField
              type="number"
              value={contributionAmount}
              onChange={(e) => setContributionAmount(e.target.value)}
              placeholder="Enter Amount"
              fullWidth
              className="contribution-section input"
            />
            <div
              className="percentage-buttons"
              style={{ justifyContent: 'space-around', display: 'flex', gap: '10px', marginTop: '10px' }}
            >
              <Button onClick={() => handlePercentageClick(0.5)} variant="outlined" color="primary">
                50%
              </Button>
              <Button onClick={() => handlePercentageClick(1)} variant="outlined" color="primary">
                Max
              </Button>
            </div>
          </Box>
          <Button
            variant="contained"
            color="primary"
            onClick={handleContribute}
            disabled={loading || !contributionAmount || parseFloat(contributionAmount) <= 0}
            className="contribution-button"
            sx={{ mt: 2 }}
            fullWidth
          >
            {loading ? <CircularProgress size={24} /> : 'Contribute'}
          </Button>
        </div>
      </div>
    );
  }, [
    miningEventStatus,
    userContributions,
    userReward,
    loading,
    handleClaimMiningReward,
    hasClaimedReward,
    totalMiners,
    contributionAmount,
    handlePercentageClick,
    handleContribute,
    setShowShareModal
  ]);

  return (
    <div className="mining-container">
      {/* Approving overlay */}
      {approving && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
            backgroundColor: 'rgba(0, 0, 0, 0.7)',
            zIndex: 1000,
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
          }}
        >
          <CircularProgress />
        </div>
      )}

      {/* Main Title */}
      <Typography
        variant="h4"
        gutterBottom
        className="title-glow"
        sx={{
          fontWeight: 700,
          letterSpacing: '0.1rem',
          textAlign: 'center',
          textTransform: 'uppercase',
          mb: 3,
          fontFamily: 'var(--font-primary)',
          fontSize: '2.5rem',
        }}
      >
        $CASH Mining
      </Typography>

      {/* Mining Reward Display */}
      <Box className="mining-reward" sx={{ my: 2 }}>
        <Typography variant="h6">Rewards:</Typography>
        <Typography
          variant="h5"
          className={`reward-amount ${hasClaimedReward ? 'claimed-reward' : ''}`}
          title={hasClaimedReward ? 'Reward already claimed' : ''}
        >
          {formatNumber(userReward || '0')} $CASH
        </Typography>

        {userBoost > 0 && (
          <Typography
            variant="body2"
            sx={{
              color: '#ffffff',
              display: 'block',
              fontSize: '0.75rem',
              mt: 1,
            }}
            gutterBottom
          >
            🚀 You have a <span style={{ color: 'limeGreen' }}>{userBoost}%</span> boost from your Booster NFTs!
          </Typography>
        )}

        {account && isEventLive && userContributions.contributionCount > 0 && (
          <Typography
            variant="caption"
            sx={{ color: '#ffffff', display: 'block', fontSize: '0.75rem', mt: 1 }}
          >
            My Contributions:{' '}
            {formatEth(userContributions.contributionAmount || '0')} ETH |
            Times Contributed: {userContributions.contributionCount} |
            Total Miners: {totalMiners}
          </Typography>
        )}

        {!isEventLive && !isEventFinalized && !hasClaimedReward && (
          <div className="button-wrapper" style={{ marginTop: '10px' }}>
            <Button
              variant="contained"
              color="primary"
              onClick={handleClaimMiningReward}
              disabled={loading || parseFloat(userReward) === 0}
              className="claim-reward-button"
              sx={{ justifyContent: 'center' }}
              fullWidth
            >
              {loading ? <CircularProgress size={24} /> : 'Claim Mining Reward'}
            </Button>
          </div>
        )}
      </Box>

      {/* Mining actions */}
      <Box className="mining-actions" sx={{ my: 2 }}>
        {isEventLive && (
          <Box className="mining-time-left" sx={{ mb: 2 }}>
            <Typography variant="h6" sx={{ fontSize: '1rem' }}>
              Time Left: {formatTimeShort(timeLeft)}
            </Typography>
          </Box>
        )}

        {renderContent()}
      </Box>

      {/* Mystery message if event live */}
      {isEventLive && (
        <Box
          className="mysterious-message glitch"
          sx={{
            mt: 3,
            textAlign: 'center',
            maxWidth: '350px',
            margin: '0 auto',
          }}
        >
          {glitchSplit('Your contributions ignite change. Empower the revolution.')}
        </Box>
      )}

      {/* Modals */}
      {showShareModal && (
        <ShareOnXModal
          onClose={() => setShowShareModal(false)}
          minerNumber={userContributions.minerNumber}
          timesMined={userContributions.contributionCount}
          miningRewards={formatNumber(userReward)}
        />
      )}
      {showMinerShareModal && (
        <ShareMinerNumberModal
          onClose={() => setShowMinerShareModal(false)}
          minerNumber={userContributions.minerNumber}
        />
      )}
      {showStakingModal && (
        <StakingConfirmationModal
          onConfirm={handleApprovalAndStake}
          onCancel={handleStakingCancel}
          rewardAmount={userReward}
          approving={approving}
        />
      )}
    </div>
  );
};

export default React.memo(Mining);